/**
 * AI Engine Service
 *
 * Maps IDE AI operations to the real AI engine endpoints (FastAPI on :5100).
 * Proxy rule: /api/ai/* → localhost:5100/*
 *
 * Engine endpoints:
 *   POST /generate  — Generate smart contract from prompt
 *   POST /audit     — Security audit on Solidity code
 *   POST /fix       — Fix compilation error
 *   POST /optimize  — Gas optimization analysis
 *   POST /explain   — Explain code in plain language
 *   POST /test      — Generate unit tests
 *   POST /memory/index — Index project files into vector memory
 *   GET  /memory/:id   — Get memory status
 */

import { post, get, resolveApiPath } from '@/lib/api';
import { buildAuthHeaders } from '@/lib/auth';

/* ================================================================ */
/*  Types                                                           */
/* ================================================================ */

export interface AiContext {
  projectId?: string;
  userId?: string;
  orgId?: string;
  files?: Record<string, string>;      // filename → content
  currentFile?: string;                 // currently open file path
  currentCode?: string;                 // currently open file content
}

export interface AiGenerateResponse {
  code: string;
  explanation: string;
  model: string;
  usage?: { input_tokens: number; output_tokens: number };
}

export interface AiAuditResponse {
  findings: AuditFinding[];
  summary: string;
  riskLevel: 'low' | 'medium' | 'high' | 'critical';
  model: string;
}

export interface AuditFinding {
  severity: 'info' | 'low' | 'medium' | 'high' | 'critical';
  title: string;
  description: string;
  location?: string;
  recommendation?: string;
}

export interface AiFixResponse {
  code: string;
  explanation: string;
  changes: string[];
  model: string;
}

export interface AiOptimizeResponse {
  code: string;
  suggestions: OptimizationSuggestion[];
  estimatedSavings: string;
  model: string;
}

export interface OptimizationSuggestion {
  title: string;
  description: string;
  gasSaved: string;
  before?: string;
  after?: string;
}

export interface AiExplainResponse {
  explanation: string;
  model: string;
}

export interface AiTestResponse {
  code: string;
  framework: string;
  explanation: string;
  model: string;
}

export interface AiCompletionResponse {
  code: string;
  model: string;
}

export interface MemoryStatus {
  project_id: string;
  chunks: number;
}

/* ================================================================ */
/*  Helper: Build request body with context                         */
/* ================================================================ */

function buildBody(ctx: AiContext, extra: Record<string, unknown> = {}) {
  return {
    project_id: ctx.projectId ?? 'default',
    user_id: ctx.userId ?? getUserId(),
    org_id: ctx.orgId ?? 'default',
    files: ctx.files ?? {},
    ...extra,
  };
}

function getUserId(): string {
  try {
    const user = localStorage.getItem('user');
    if (user) {
      const parsed = JSON.parse(user);
      return parsed.id ?? 'anonymous';
    }
  } catch { /* ignore */ }
  return 'anonymous';
}

/* ================================================================ */
/*  Core AI Operations                                              */
/* ================================================================ */

/**
 * Generate a smart contract from a natural language prompt
 */
export async function generate(
  prompt: string,
  ctx: AiContext = {},
): Promise<AiGenerateResponse> {
  return post<AiGenerateResponse>('/ai/generate', buildBody(ctx, { prompt }));
}

/**
 * Run a security audit on Solidity code
 */
export async function audit(
  code: string,
  ctx: AiContext = {},
): Promise<AiAuditResponse> {
  return post<AiAuditResponse>('/ai/audit', buildBody(ctx, { code }));
}

/**
 * Fix a compilation error — sends code + error to AI, returns fixed code
 */
export async function fix(
  code: string,
  error: string,
  ctx: AiContext = {},
): Promise<AiFixResponse> {
  return post<AiFixResponse>('/ai/fix', buildBody(ctx, { code, error }));
}

/**
 * Analyze code for gas optimization
 */
export async function optimize(
  code: string,
  ctx: AiContext = {},
): Promise<AiOptimizeResponse> {
  return post<AiOptimizeResponse>('/ai/optimize', buildBody(ctx, { code }));
}

/**
 * Explain code in plain language
 */
export async function explain(
  code: string,
  ctx: AiContext = {},
): Promise<AiExplainResponse> {
  return post<AiExplainResponse>('/ai/explain', buildBody(ctx, { code }));
}

/**
 * Generate unit tests for a contract
 */
export async function generateTests(
  code: string,
  ctx: AiContext = {},
): Promise<AiTestResponse> {
  return post<AiTestResponse>('/ai/test', buildBody(ctx, { code }));
}

/**
 * Code completion — used by Monaco inline completions
 * Maps to /generate with a completion-style prompt
 */
export async function complete(
  prefix: string,
  suffix: string,
  fileName: string,
  ctx: AiContext = {},
): Promise<string> {
  const prompt = [
    'Complete the following Solidity code. Return ONLY the completion code, no explanation, no markdown fences.',
    '',
    '// File: ' + fileName,
    '// Context before cursor:',
    prefix.slice(-800),
    '',
    '// Context after cursor:',
    suffix.slice(0, 300),
    '',
    'Complete from where the code stops:',
  ].join('\n');

  const res = await post<AiGenerateResponse>('/ai/generate', buildBody(ctx, { prompt }));
  return res.code ?? '';
}

/* ================================================================ */
/*  Memory / Context Management                                     */
/* ================================================================ */

/**
 * Index project files into vector memory for AI context enrichment
 */
export async function indexMemory(
  projectId: string,
  files: Record<string, string>,
): Promise<{ indexed: number; project_id: string }> {
  return post('/ai/memory/index', { project_id: projectId, files });
}

/**
 * Get memory status for a project
 */
export async function getMemoryStatus(projectId: string): Promise<MemoryStatus> {
  return get<MemoryStatus>(`/ai/memory/${projectId}`);
}

/* ================================================================ */
/*  Vibe Streaming — POST /ai/vibe (SSE)                           */
/* ================================================================ */

export type VibeToken = { type: 'token'; value: string };
export type VibeDone  = { type: 'done'; usage: { inputTokens: number; outputTokens: number } };
export type VibeError = { type: 'error'; message: string };
export type VibeEvent = VibeToken | VibeDone | VibeError;

/**
 * Open an SSE stream to /ai/vibe.
 * Calls `onToken` for each text chunk received, then resolves when done.
 * Returns the accumulated full text.
 */
export async function vibeStream(
  prompt: string,
  ctx: AiContext = {},
  onToken?: (token: string) => void,
): Promise<{ text: string; inputTokens: number; outputTokens: number }> {
  const res = await fetch(resolveApiPath('/ai/vibe'), {
    method: 'POST',
    headers: buildAuthHeaders({
      'Content-Type': 'application/json',
    }),
    credentials: 'include',
    body: JSON.stringify({
      prompt,
      context: ctx.currentCode ? ctx.currentCode.slice(0, 8000) : undefined,
    }),
  });

  if (!res.ok || !res.body) {
    throw new Error(`Vibe stream failed: HTTP ${res.status}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let text = '';
  let inputTokens = 0;
  let outputTokens = 0;

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;
      try {
        const event = JSON.parse(line.slice(6)) as VibeEvent;
        if (event.type === 'token') {
          text += event.value;
          onToken?.(event.value);
        } else if (event.type === 'done') {
          inputTokens  = event.usage.inputTokens;
          outputTokens = event.usage.outputTokens;
        } else if (event.type === 'error') {
          throw new Error(event.message);
        }
      } catch (e) {
        if (e instanceof SyntaxError) continue; // non-JSON data line
        throw e;
      }
    }
  }

  return { text, inputTokens, outputTokens };
}
